/**
 * LAURYE — Product Detail Form & Variant Switcher
 * Full Shopify OS 2.0 dynamic variant switching & AJAX cart integration
 */

class ProductForm {
  constructor() {
    this.form = document.querySelector('#product-form-main') || document.querySelector('.product-form') || document.querySelector('[data-product-form]') || document.querySelector('form[action*="/cart/add"]');
    if (!this.form) return;

    this.variantOptions = document.querySelectorAll('[data-variant-option]');
    this.priceElement = document.querySelector('[data-product-price]');
    this.comparePriceElement = document.querySelector('[data-product-compare-price]');
    this.saveBadge = document.querySelector('.product-price-large .price-save-badge');
    this.variantIdInput = document.querySelector('[data-variant-id-input]');
    this.selectElement = document.querySelector('select[name="id"]');
    this.atcButton = document.querySelector('[data-main-atc-btn]');
    this.mainImage = document.querySelector('[data-product-main-image]');
    this.stickyATCPrice = document.querySelector('.sticky-atc-price');

    // Parse product data
    const jsonEl = document.querySelector('[data-product-json]');
    if (jsonEl) {
      try {
        this.productData = JSON.parse(jsonEl.textContent);
      } catch (e) {
        this.productData = null;
      }
    }

    this.init();
  }

  init() {
    this.variantOptions.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const parentGroup = btn.closest('.product-variant-group');
        if (!parentGroup) return;

        parentGroup.querySelectorAll('.variant-option-btn').forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');

        const valLabel = parentGroup.querySelector('.variant-selected-value');
        if (valLabel) {
          valLabel.textContent = btn.dataset.optionValue;
        }

        this.onVariantChange();
      });
    });
  }

  getSelectedOptions() {
    const selected = [];
    const groups = document.querySelectorAll('.product-variant-group');
    groups.forEach(group => {
      const selectedBtn = group.querySelector('.variant-option-btn.selected');
      if (selectedBtn) {
        selected.push(selectedBtn.dataset.optionValue);
      }
    });
    return selected;
  }

  onVariantChange() {
    const selectedOptions = this.getSelectedOptions();
    if (!selectedOptions.length) return;

    let matchedVariant = null;

    const variantsList = Array.isArray(this.productData)
      ? this.productData
      : (this.productData && this.productData.variants ? this.productData.variants : []);

    if (variantsList.length > 0) {
      matchedVariant = variantsList.find(variant => {
        if (!variant || !variant.options) return false;
        return variant.options.every((optVal, idx) => optVal === selectedOptions[idx]);
      });
    }

    // Fallback: check select options
    if (!matchedVariant && this.selectElement) {
      const optionEls = Array.from(this.selectElement.options);
      const match = optionEls.find(opt => {
        const opt1 = opt.dataset.option1;
        const opt2 = opt.dataset.option2;
        const opt3 = opt.dataset.option3;
        if (selectedOptions.length === 1) return opt1 === selectedOptions[0];
        if (selectedOptions.length === 2) return opt1 === selectedOptions[0] && opt2 === selectedOptions[1];
        if (selectedOptions.length === 3) return opt1 === selectedOptions[0] && opt2 === selectedOptions[1] && opt3 === selectedOptions[2];
        return false;
      });

      if (match) {
        matchedVariant = {
          id: match.value,
          price: match.dataset.price,
          compare_at_price: match.dataset.comparePrice,
          available: match.dataset.available === 'true',
          featured_image: match.dataset.imageSrc ? { src: match.dataset.imageSrc } : null
        };
      }
    }

    if (matchedVariant) {
      this.updateVariantDetails(matchedVariant);
    }
  }

  formatMoney(cents) {
    if (typeof cents === 'string' && cents.includes('€')) return cents;
    const euros = (cents / 100).toFixed(2).replace('.', ',');
    return `${euros} €`;
  }

  updateVariantDetails(variant) {
    // 1. Update Hidden Variant ID & Select
    if (this.variantIdInput) {
      this.variantIdInput.value = variant.id;
    }
    if (this.selectElement) {
      this.selectElement.value = variant.id;
    }

    // 2. Update Price Display
    const formattedPrice = typeof variant.price === 'number' ? this.formatMoney(variant.price) : variant.price;
    if (this.priceElement && formattedPrice) {
      this.priceElement.textContent = formattedPrice;
    }
    if (this.stickyATCPrice && formattedPrice) {
      this.stickyATCPrice.textContent = formattedPrice;
    }

    // 3. Update Compare at Price & Discount Badge
    if (this.comparePriceElement) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const formattedCompare = typeof variant.compare_at_price === 'number' ? this.formatMoney(variant.compare_at_price) : variant.compare_at_price;
        this.comparePriceElement.textContent = formattedCompare;
        this.comparePriceElement.style.display = 'inline';

        if (this.saveBadge && typeof variant.price === 'number' && typeof variant.compare_at_price === 'number') {
          const discount = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          this.saveBadge.textContent = `-${discount}%`;
          this.saveBadge.style.display = 'inline-block';
        }
      } else {
        this.comparePriceElement.style.display = 'none';
        if (this.saveBadge) this.saveBadge.style.display = 'none';
      }
    }

    // 4. Update ATC Button State
    if (this.atcButton) {
      if (variant.available) {
        this.atcButton.removeAttribute('disabled');
        this.atcButton.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg>
          Adicionar ao carrinho
        `;
      } else {
        this.atcButton.setAttribute('disabled', 'disabled');
        this.atcButton.innerHTML = 'Esgotado';
      }
    }

    // 5. Update Main Image if variant has an image
    if (variant.featured_image && variant.featured_image.src && this.mainImage) {
      this.mainImage.src = variant.featured_image.src;
    }

    // 6. Update URL parameter
    try {
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    } catch (e) {}
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ProductForm();
});
